import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    Grid,
    Divider,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from "@mui/material";
import { columns, Get_Customer_Report_URI } from "./config";
import CustomDataGrid from "../../Components/CustomDataGrid";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useParams, useNavigate } from "react-router-dom";
import RemoveRedEye from "@mui/icons-material/RemoveRedEye";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { useReactToPrint } from "react-to-print";
import "./customerReport.css";
import InvoiceDetail from "./InvoiceDetail"; // Import the InvoiceDetail component

const CustomerReport = () => {
    const [customerData, setCustomerData] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [expandedInvoices, setExpandedInvoices] = useState({});
    const [summaryData, setSummaryData] = useState({
        totalPurchases: 0,
        totalPaid: 0,
        totalRemaining: 0,
        invoiceCount: 0,
    });
    const [isLoading, setIsLoading] = useState(true);
    const { id: customerId } = useParams();
    const authHeader = useAuthHeader();
    const navigate = useNavigate();
    const printComponentRef = useRef();

    // Setup the print functionality using the hook
    const handlePrint = useReactToPrint({
        contentRef: printComponentRef,
        documentTitle: `Customer Report - ${customerData?.name || "Customer"}`,
        pageStyle:
            "@page { size: auto; margin: 10mm; } @media print { body { margin: 0; padding: 20px; } }",
        onAfterPrint: () => toast.success("Report printed successfully!"),
    });

    console.log("test");
    // Actions for the data grid
    const actions = [
        {
            title: "View Invoice",
            color: "info",
            handler: (id) => {
                navigate("/Invoice/" + id);
            },
            parameters: ["id"],
            icon: <RemoveRedEye color="info" />,
        },
    ];

    // Fetch customer data and invoices
    useEffect(() => {
        const fetchCustomerData = async () => {
            setIsLoading(true);
            try {
                // Fetch customer details
                const customerResponse = await fetch(
                    `${REACT_APP_BACKEND_URL}/otherparty/${customerId}`,
                    {
                        headers: {
                            Authorization: authHeader,
                        },
                    }
                );

                if (!customerResponse.ok) {
                    throw new Error("Failed to fetch customer data");
                }

                const customerResult = await customerResponse.json();
                console.log("customerResult", customerResult);
                if (customerResult.otherparty) {
                    setCustomerData(customerResult.otherparty);
                } else {
                    toast.error("Could not load customer data");
                }

                // Fetch customer invoices
                const invoicesResponse = await fetch(
                    `${REACT_APP_BACKEND_URL}${Get_Customer_Report_URI(
                        customerId
                    )}`,
                    {
                        headers: {
                            Authorization: authHeader,
                        },
                    }
                );

                if (!invoicesResponse.ok) {
                    throw new Error("Failed to fetch customer invoices");
                }
                const invoicesResult = await invoicesResponse.json();
                if (invoicesResult.success) {
                    setInvoices(invoicesResult.data.invoices);
                    setSummaryData(invoicesResult.data.summary);

                    // Initialize expanded state for each invoice
                    const initialExpandedState = {};
                    invoicesResult.data.invoices.forEach((invoice) => {
                        // Initially expand only unpaid invoices
                        initialExpandedState[invoice.id] =
                            Number(invoice.remainingAmount) > 0;
                    });
                    setExpandedInvoices(initialExpandedState);
                } else {
                    toast.error("Could not load customer invoices");
                }
            } catch (error) {
                console.error("Error fetching customer report data:", error);
                toast.error("Error loading customer report");
            } finally {
                setIsLoading(false);
            }
        };

        if (customerId) {
            fetchCustomerData();
        }
    }, [customerId, authHeader]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    // Group invoices by month/year for the summary table
    const getMonthlyData = () => {
        const monthlyData = {};

        invoices.forEach((invoice) => {
            const date = new Date(invoice.createdAt);
            const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

            if (!monthlyData[monthYear]) {
                monthlyData[monthYear] = {
                    month: monthYear,
                    total: 0,
                    paid: 0,
                    remaining: 0,
                    count: 0,
                };
            }

            monthlyData[monthYear].total += Number(invoice.priceFinal);
            monthlyData[monthYear].paid += Number(invoice.paidAmount);
            monthlyData[monthYear].remaining += Number(invoice.remainingAmount);
            monthlyData[monthYear].count += 1;
        });

        return Object.values(monthlyData).sort((a, b) => {
            const [aMonth, aYear] = a.month.split("/");
            const [bMonth, bYear] = b.month.split("/");
            return bYear - aYear || bMonth - aMonth;
        });
    };
    console.log("customerData", customerData);
    // Function to toggle expanded state of an invoice
    const toggleInvoiceExpansion = (invoiceId) => {
        setExpandedInvoices((prev) => ({
            ...prev,
            [invoiceId]: !prev[invoiceId],
        }));
    };

    // Function to toggle all invoices expanded state
    const toggleAllInvoices = () => {
        const allExpanded = Object.values(expandedInvoices).every(
            (value) => value
        );
        const newExpandedState = {};

        invoices.forEach((invoice) => {
            newExpandedState[invoice.id] = !allExpanded;
        });

        setExpandedInvoices(newExpandedState);
    };

    return (
        <Box sx={{ p: 3 }}>
            {" "}
            <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
            >
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/CustomerList")}
                    className="noprint"
                >
                    Back to Customers
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PrintIcon />}
                    className="noprint"
                    onClick={handlePrint}
                >
                    Print Report
                </Button>
            </Box>
            <div>
                <Box className="report-header">
                    <Typography variant="h4" gutterBottom>
                        Customer Purchase Report
                    </Typography>
                    <Typography variant="subtitle1" className="report-date">
                        Report Date: {new Date().toLocaleDateString()}
                    </Typography>
                </Box>

                {customerData && (
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Typography variant="h5" gutterBottom>
                            Customer Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box className="customer-info-item">
                                    <Typography variant="subtitle2">
                                        Name:
                                    </Typography>
                                    <Typography variant="body1">
                                        {customerData.name}
                                    </Typography>
                                </Box>
                                <Box className="customer-info-item">
                                    <Typography variant="subtitle2">
                                        Phone:
                                    </Typography>
                                    <Typography variant="body1">
                                        {customerData.phone || "Not Available"}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box className="customer-info-item">
                                    <Typography variant="subtitle2">
                                        Address:
                                    </Typography>
                                    <Typography variant="body1">
                                        {customerData.address ||
                                            "Not Available"}
                                    </Typography>
                                </Box>
                                <Box className="customer-info-item">
                                    <Typography variant="subtitle2">
                                        Tax Number:
                                    </Typography>
                                    <Typography variant="body1">
                                        {customerData.taxNumber ||
                                            "Not Available"}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Card elevation={2} className="summary-card">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Summary
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box className="summary-item">
                                            <Typography variant="subtitle2">
                                                Total Purchases:
                                            </Typography>
                                            <Typography variant="h6">
                                                {summaryData.totalPurchases.toFixed(
                                                    2
                                                )}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box className="summary-item">
                                            <Typography variant="subtitle2">
                                                Total Paid:
                                            </Typography>
                                            <Typography variant="h6">
                                                {summaryData.totalPaid.toFixed(
                                                    2
                                                )}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box className="summary-item">
                                            <Typography variant="subtitle2">
                                                Total Remaining:
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                color="error"
                                            >
                                                {summaryData.totalRemaining.toFixed(
                                                    2
                                                )}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box className="summary-item">
                                            <Typography variant="subtitle2">
                                                Total Invoices:
                                            </Typography>
                                            <Typography variant="h6">
                                                {summaryData.invoiceCount}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card elevation={2} className="summary-card">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Monthly Breakdown
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    Month/Year
                                                </TableCell>
                                                <TableCell align="right">
                                                    Invoices
                                                </TableCell>
                                                <TableCell align="right">
                                                    Amount
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {getMonthlyData()
                                                .slice(0, 5)
                                                .map((row) => (
                                                    <TableRow key={row.month}>
                                                        <TableCell>
                                                            {row.month}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.count}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.total.toFixed(
                                                                2
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Paper
                    sx={{ p: 2, mb: 4 }}
                    elevation={3}
                    ref={printComponentRef}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "col",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h5" gutterBottom>
                            Invoice History
                        </Typography>
                        <CustomDataGrid
                            rows={invoices}
                            columns={columns}
                            actions={actions}
                            loading={isLoading}
                            getRowClassName={(params) =>
                                Number(params.row.remainingAmount) > 0
                                    ? "unpaid-row"
                                    : "paid-row"
                            }
                        />{" "}
                    </Box>
                </Paper>

                <Paper sx={{ p: 2, mb: 4 }} elevation={3}>
                    <Typography variant="h5" gutterBottom>
                        Invoice Details
                    </Typography>

                    {invoices.length === 0 ? (
                        <Typography variant="body1">
                            No invoices found for this customer.
                        </Typography>
                    ) : (
                        <>
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={toggleAllInvoices}
                                    className="noprint"
                                >
                                    {Object.values(expandedInvoices).every(
                                        (val) => val
                                    )
                                        ? "Collapse All"
                                        : "Expand All"}
                                </Button>
                            </Box>

                            {invoices.map((invoice) => (
                                <InvoiceDetail
                                    key={invoice.id}
                                    invoice={invoice}
                                    formatDate={formatDate}
                                />
                            ))}
                        </>
                    )}
                </Paper>
                {/* End of New Invoice Details Section */}
            </div>
        </Box>
    );
};

export default CustomerReport;
